import React from 'react';
import {
  Badge, Box, Collapse, HStack, Text,
} from '@chakra-ui/react';
import { FiMessageSquare } from 'react-icons/fi';
import { NIL } from 'uuid';
import PageHeader from './PageHeader';
import AuroraBackground from './dashboard/AuroraBackground';
import GlassGridPanel from './GlassGridPanel';
import ChatPanel from './ChatPanel';
import { useChatUnread } from '../hooks/useChatRead';
import SlackLinkButton from './SlackLinkButton';
import ChatFocusControls from './ChatFocusControls';

// item 193 — the project's CHAT thread (REPURPOSED from the old project notes). The team
// talks about the project here and attaches files per message (ChatPanel handles the
// in-DB StoredFile upload). Append-only with per-user read state — the chat backbone
// (items 99/158/159) scoped to this project's thread (ThreadKey = projectId). The broader
// per-project document store lives in the separate Documents section (item 129b).
const ProjectChatSection: React.FC<{
  projectId: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
}> = ({ projectId, collapse }) => {
  const enabled = projectId !== NIL;
  const { unreadCount } = useChatUnread(enabled ? projectId : undefined);
  const collapsed = collapse?.collapsed ?? false;

  return (
    <Box
      position="relative"
      mx={{ base: -4, md: -10 }}
      px={{ base: 4, md: 10 }}
      pt={collapsed ? 1 : { base: 2, md: 3 }}
      pb={collapsed ? 1 : { base: 2, md: 3 }}
    >
      <AuroraBackground />
      <Box position="relative" zIndex={1}>
        <PageHeader
          dense
          onTitleClick={collapse?.onToggle}
          collapsed={collapsed}
          icon={<FiMessageSquare />}
          title="Project Talk"
          subtitle="Team conversation"
          actions={(!collapsed || unreadCount > 0) ? (
            <HStack flexWrap="wrap" sx={{ rowGap: '6px' }} spacing={2}>
              {/* item 400 — ONE control row (Slack settings → Focus → Undock), hidden
                  while collapsed so folded sections read identically everywhere */}
              {!collapsed && (
                <>
                  <SlackLinkButton threadKey={projectId} suggestedName="basix-project" />
                  <ChatFocusControls threadKey={projectId} title="Project Talk" />
                </>
              )}
              {unreadCount > 0 && (
                <Badge colorScheme="green" borderRadius="full" px={2}>{`${unreadCount} new`}</Badge>
              )}
            </HStack>
          ) : undefined}
        />
        <Collapse in={!collapsed} animateOpacity unmountOnExit>
          <GlassGridPanel>
            {enabled ? (
              <ChatPanel threadKey={projectId} />
            ) : (
              <Text fontSize="sm" color="gray.500">Save the project to start its chat thread.</Text>
            )}
          </GlassGridPanel>
        </Collapse>
      </Box>
    </Box>
  );
};

ProjectChatSection.defaultProps = {
  collapse: undefined,
};

export default ProjectChatSection;
